import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useResponsiveLayout } from './useResponsiveLayout';
import { useOrganizationContext } from '../service/context/organizationContext';
import { useUserDetailContext } from '../service/context/userDetailContext';
import {
  GetHarmReportsResponse,
  HarmReportAdvancedFilters,
  HarmReportsFilter,
  HarmReportsKebabOption,
  HarmReportsPendingAction,
} from '../pages/harmReports/harmReports.type';
import { reportService } from '../service/instances';
import {
  GetReportsByFilterRequest,
  HarmReport,
  PlayerSummary,
  ReportStatus,
  EventType,
} from '@inplay/models';
import { DateRange } from 'react-day-picker';
import { toastV2 } from '../component/toastV2/toastV2';
import { useTranslation } from 'react-i18next';
import { validateReportsForDeletion } from '../pages/harmReports/harmReportesHelper';
import { FormPlayerData } from '../component/harmReportForm/harmReportForm.types';
import {
  PlayerType,
  NewPlayerImages,
} from '../component/selectPlayerModal/selectPlayerModal.types';
import {
  createFormPlayerDataFromExistingPlayer,
  createFormPlayerDataFromNewPlayer,
} from '../utils/createFormPlayerData';
import { useNavigate } from 'react-router-dom';
import { SelectedUser } from '../component/userVerificationModal/userVerificationModal.types';
import { route } from '../service/router';
import { getErrorMessage } from '../utils/helper';
import { hasActiveAdvancedFilters } from '../utils/hasActiveAdvancedFilters';
import { filterHarmReports } from '../utils/filterHarmReports';
import { useWebsocketSubscription } from './useWebsocketSubscription';

export const useHarmReports = () => {
  const navigate = useNavigate();
  const { selectedVenueBranch, loading: organizationContextLoading } =
    useOrganizationContext();
  const { userDetail } = useUserDetailContext();
  const { isTablet } = useResponsiveLayout();
  const { t } = useTranslation();

  const [allHarmReports, setAllHarmReports] = useState<HarmReport[]>([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState<HarmReportsFilter>({
    searchQuery: '',
    selectedDateRange: { from: new Date(), to: new Date() },
    status: [ReportStatus.ACTIVE],
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const abortControllerRef = useRef<AbortController | null>(null);

  const [selectedRows, setSelectedRows] = useState<HarmReport[]>([]);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [openCreateHarm, setOpenCreateHarm] = useState(false);
  const [isReviewAndEditMode, setIsReviewAndEditMode] = useState(false);

  const [selectedHarmReport, setSelectedHarmReport] =
    useState<HarmReport | null>(null);
  const handleRowClick = (row: HarmReport) => {
    setSelectedHarmReport(row);
  };
  const [harmReportForCreate, setHarmReportForCreate] =
    useState<HarmReport | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUserVerification, setShowUserVerification] =
    useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [verifiedUser, setVerifiedUser] = useState<SelectedUser | null>(null);
  const [showSelectPlayerModal, setShowSelectPlayerModal] =
    useState<boolean>(false);

  const handleOnVerify = (user: SelectedUser) => {
    setVerifiedUser(user);
    setShowUserVerification(false);

    switch (pendingAction) {
      case HarmReportsPendingAction.CREATE_HARM_REPORT:
        setShowSelectPlayerModal(true);
        break;
      case HarmReportsPendingAction.DELETE_REPORTS:
        setShowDeleteConfirmation(true);
        setPendingAction(null);
        break;
      case HarmReportsPendingAction.EDIT_REPORT:
        setOpenCreateHarm(true);
        setPendingAction(null);
        break;
      case HarmReportsPendingAction.REVIEW_AND_SIGN:
        setOpenCreateHarm(true);
        setIsReviewAndEditMode(true);
        setPendingAction(null);
        break;
      default:
        break;
    }
  };

  const [playerData, setPlayerData] = useState<FormPlayerData | null>(null);

  const handleOnProceed: (
    playerOrImages: PlayerSummary | NewPlayerImages,
    playerType: PlayerType,
  ) => void = (
    playerOrImages: PlayerSummary | NewPlayerImages,
    playerType: PlayerType,
  ): void => {
    setShowSelectPlayerModal(false);

    if (playerType === PlayerType.ExistingPlayer) {
      const player: PlayerSummary = playerOrImages as PlayerSummary;
      setPlayerData(createFormPlayerDataFromExistingPlayer(player));
    } else {
      const images: NewPlayerImages = playerOrImages as NewPlayerImages;
      setPlayerData(createFormPlayerDataFromNewPlayer(images));
    }

    switch (pendingAction) {
      case HarmReportsPendingAction.CREATE_HARM_REPORT:
        setOpenCreateHarm(true);
        setPendingAction(null);
        break;
      default:
        break;
    }
  };

  const closeHarmReportDetails = () => {
    setSelectedHarmReport(null);
  };

  const fetchHarmReports = useCallback(
    async (override?: Partial<HarmReportsFilter>, fromSocket?: boolean) => {
      if (!selectedVenueBranch) return;

      abortControllerRef.current?.abort();
      const abortController: AbortController = new AbortController();
      abortControllerRef.current = abortController;

      // signal.aborted covers both supersession and unmount — guards every setState.
      // Loader ON only for user requests (no flash); OFF whenever the current
      // (non-aborted) request settles regardless of owner — else a silent socket
      // fetch superseding a user fetch would stick the loader ON.
      const shouldShowLoader: boolean = !fromSocket;

      const filtersToUse = { ...filtersRef.current, ...override };
      if (shouldShowLoader) setLoading(true);
      try {
        const query: GetReportsByFilterRequest = {
          venueBranchId: selectedVenueBranch._id,
          currentDate: filtersToUse.selectedDateRange.from ?? new Date(),
          startDate: filtersToUse.selectedDateRange.from,
          endDate:
            filtersToUse.selectedDateRange.to ??
            filtersToUse.selectedDateRange.from,
          status: filtersToUse.status,
        };
        const response: GetHarmReportsResponse =
          await reportService.getHarmReports(query, abortController.signal);
        if (abortController.signal.aborted) return;
        const harmReports: HarmReport[] = response.data?.harmReports ?? [];
        setAllHarmReports(harmReports);
      } catch (error) {
        if (abortController.signal.aborted) return;
        if (fromSocket) {
          console.error('[useHarmReports] silent refetch failed', error);
        } else {
          console.error('Error fetching harm reports:', error);
          toastV2.error('Failed to fetch harm reports.');
        }
      } finally {
        if (!abortController.signal.aborted) setLoading(false);
      }
    },
    [selectedVenueBranch],
  );

  useEffect(() => {
    fetchHarmReports();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [selectedVenueBranch]);

  const filteredReports: HarmReport[] = useMemo(
    (): HarmReport[] => filterHarmReports(allHarmReports, filters),
    [allHarmReports, filters],
  );
  useWebsocketSubscription(selectedVenueBranch?._id, [EventType.HARM], () =>
    fetchHarmReports(undefined, true),
  );

  const harmReports: HarmReport[] = useMemo((): HarmReport[] => {
    const searchQuery: string = filters.searchQuery?.toLowerCase();

    if (!searchQuery) return filteredReports;

    return filteredReports.filter(
      (report: HarmReport): boolean =>
        report.reportRefNo?.toLowerCase().includes(searchQuery) ||
        report.playerProfile.id?.toLowerCase().includes(searchQuery) ||
        report.createdBy?.firstName?.toLowerCase().includes(searchQuery) ||
        report.createdBy?.lastName?.toLowerCase().includes(searchQuery) ||
        report.playerDetails.description?.toLowerCase().includes(searchQuery),
    );
  }, [filteredReports, filters.searchQuery]);

  const activeFiltersExist: boolean = useMemo(
    () => hasActiveAdvancedFilters(filters),
    [filters],
  );

  const handleOnChangeSearch = (query: string) => {
    setFilters((f) => ({ ...f, searchQuery: query }));
  };

  const handleClearFilters = (): void => {
    setFilters((prev: HarmReportsFilter) => ({
      searchQuery: prev.searchQuery,
      selectedDateRange: prev.selectedDateRange,
      status: prev.status,
    }));
    setOpenFilterModal(false);
  };

  const handleApplyFilters = (
    advancedFilters: HarmReportAdvancedFilters,
    newDateRange?: DateRange,
  ): void => {
    const updatedFilters: HarmReportsFilter = {
      ...filters,
      ...advancedFilters,
    };

    if (newDateRange) {
      updatedFilters.selectedDateRange = newDateRange;
    }

    setFilters(updatedFilters);
    setOpenFilterModal(false);

    const dateChanged: boolean = Boolean(
      newDateRange &&
      (newDateRange.from?.getTime() !==
        filters.selectedDateRange?.from?.getTime() ||
        newDateRange.to?.getTime() !==
          filters.selectedDateRange?.to?.getTime()),
    );

    if (dateChanged) {
      fetchHarmReports({ selectedDateRange: newDateRange });
    }
  };

  const handleDateRangeChange = (range: DateRange) => {
    const selectedDateRange: DateRange = {
      from: range.from,
      to: range.to ?? range.from,
    };
    setFilters((prev) => ({ ...prev, selectedDateRange }));
    fetchHarmReports({ selectedDateRange });
  };

  const handleReportHarmClick = () => {
    setPendingAction(HarmReportsPendingAction.CREATE_HARM_REPORT);
    setShowUserVerification(true);
  };

  const handleReportHarmClose = () => {
    setOpenCreateHarm(false);
    setVerifiedUser(null);
    setHarmReportForCreate(null);
    setPlayerData(null);
  };

  const toggleFilterModal = () => setOpenFilterModal((v) => !v);

  const handleSelectionChange = (rows: HarmReport[]) => {
    setSelectedRows(rows);
  };

  const handleDeleteHarmReports = async () => {
    try {
      if (!selectedRows.length) return;

      const { valid, invalid } = validateReportsForDeletion(
        selectedRows,
        userDetail,
      );

      if (invalid.length > 0) {
        toastV2.error(invalid[0]); // Show first validation error
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      if (!valid.length) {
        toastV2.error('No valid reports selected for deletion.');
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      const originalReportIds = valid
        .map((row) => row.originalReportId)
        .filter((id): id is string => Boolean(id));

      if (!originalReportIds.length) {
        toastV2.error('No valid report IDs found.');
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      setDeleting(true);
      const response = await reportService.deleteHarmReports(originalReportIds);
      const count = response.deletedHarmReportsCount;

      toastV2.success(
        `Successfully deleted ${count} harm report${count === 1 ? '' : 's'}`,
      );

      setSelectedRows([]);
      setShowDeleteConfirmation(false);
      await fetchHarmReports(filters);
    } catch (error: unknown) {
      const errorMessage: string = getErrorMessage(error);
      console.error(errorMessage);
      toastV2.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    setPendingAction(HarmReportsPendingAction.DELETE_REPORTS);
    setShowUserVerification(true);
  };

  const handleKebabActionFromKebabMenu = async (
    action: HarmReportsKebabOption,
    row: HarmReport,
    rowIndex: number,
  ): Promise<void> => {
    console.log('[useHarmReports][handleKebabActionFromKebabMenu] ENTRY', {
      action,
      row,
      rowIndex,
    });
    switch (action) {
      case HarmReportsKebabOption.VIEW:
        setSelectedHarmReport(row);
        return;
      case HarmReportsKebabOption.EDIT:
        setHarmReportForCreate(row);
        setPendingAction(HarmReportsPendingAction.EDIT_REPORT);
        setShowUserVerification(true);
        setIsReviewAndEditMode(false);
        return;
      case HarmReportsKebabOption.REVIEW_AND_SIGN:
        setHarmReportForCreate(row);
        setPendingAction(HarmReportsPendingAction.REVIEW_AND_SIGN);
        setShowUserVerification(true);
        return;
      case HarmReportsKebabOption.DOWNLOAD:
      case HarmReportsKebabOption.HISTORY:
        toastV2.warning('Feature not yet available');
        return;
      case HarmReportsKebabOption.DELETE:
        setSelectedRows([row]);
        setPendingAction(HarmReportsPendingAction.DELETE_REPORTS);
        setShowUserVerification(true);
        return;
      default: {
        console.error('Invalid Harm Report Kebab Option');
        const _exhaustiveCheck: never = action;
        return _exhaustiveCheck;
      }
    }
  };

  const handleSettingMenuSelect = (label: string | undefined) => {
    console.log({ label });
    if (!label) return;
    if (label === 'harmReports.settingOption.download') {
      toastV2.info(t('common.functionalityComingSoon'));
    } else if (label === 'harmReports.settingOption.deletedReports') {
      navigate(route.deletedHarmReports);
    }
  };

  const handleReviewAndSignClick = () => {
    if (!selectedHarmReport) return;
    setHarmReportForCreate(selectedHarmReport);
    setPendingAction(HarmReportsPendingAction.REVIEW_AND_SIGN);
    setShowUserVerification(true);
    setSelectedHarmReport(null);
  };

  return {
    harmReports,
    loading,
    filters,
    selectedRows,
    setSelectedRows,
    openFilterModal,
    openCreateHarm,
    isReviewAndEditMode,
    isTablet,
    selectedVenueBranch,
    fetchHarmReports,
    handleOnChangeSearch,
    handleApplyFilters,
    handleClearFilters,
    handleDateRangeChange,
    handleSelectionChange,
    handleReportHarmClick,
    handleReportHarmClose,
    handleKebabActionFromKebabMenu,
    handleDeleteHarmReports,
    toggleFilterModal,
    activeFiltersExist,
    selectedHarmReport,
    handleRowClick,
    closeHarmReportDetails,
    handleReviewAndSignClick,
    organizationContextLoading,
    harmReportForCreate,
    t,
    handleDeleteClick,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleting,
    showUserVerification,
    setShowUserVerification,
    handleOnVerify,
    handleOnProceed,
    setShowSelectPlayerModal,
    showSelectPlayerModal,
    handleSettingMenuSelect,
    verifiedUser,
    userDetail,
    playerData,
  };
};
